import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Esito di un controllo (Cribis / World Check). Valore condiviso FE/BE (inglese).
 * L'etichetta UI per IN_PROGRESS è "Approfondimenti in corso".
 */
export type CheckOutcome = 'OK' | 'KO' | 'IN_PROGRESS';

/** Allegato della sezione "Controlli e Revisione". */
export interface ReviewControlDocument {
  id: number;
  fileUrl: string;
  fileName: string | null;
  title: string | null;
  fileSize: number | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
}

/** Stato completo della sezione "Controlli e Revisione" di un fiduciario. */
export interface ReviewControl {
  id: number | null;
  professionalId: string;
  notes: string | null;
  verificationDate: string | null; // ISO date (yyyy-MM-dd)
  cribisOutcome: CheckOutcome | null;
  wcOutcome: CheckOutcome | null;
  documents: ReviewControlDocument[];
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Payload di upsert (note, data verifica, esiti). Campi opzionali. */
export interface ReviewControlRequest {
  notes?: string | null;
  verificationDate?: string | null; // ISO date (yyyy-MM-dd)
  cribisOutcome?: CheckOutcome | null;
  wcOutcome?: CheckOutcome | null;
}

/**
 * Servizio per la sezione "Controlli e Revisione" del fiduciario
 * (note interne, data verifica, esiti Cribis/WC, allegati PDF).
 */
@Injectable({
  providedIn: 'root'
})
export class ReviewControlService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = 'api/v1/professionals';

  private baseUrl(professionalId: string): string {
    return `${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/review-control`;
  }

  /** Carica i controlli del fiduciario (vuoti se non ancora creati). */
  getReviewControl(professionalId: string): Observable<ReviewControl> {
    return this.http.get<ReviewControl>(this.baseUrl(professionalId));
  }

  /** Crea/aggiorna note, data verifica ed esiti. */
  upsertReviewControl(professionalId: string, request: ReviewControlRequest): Observable<ReviewControl> {
    return this.http.put<ReviewControl>(this.baseUrl(professionalId), request);
  }

  /** Carica un allegato PDF (max 100 MB) con titolo descrittivo. */
  uploadDocument(professionalId: string, file: File, title: string): Observable<ReviewControlDocument> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('title', title ?? '');
    return this.http.post<ReviewControlDocument>(`${this.baseUrl(professionalId)}/documents`, formData);
  }

  /** Rimuove un allegato. */
  deleteDocument(professionalId: string, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl(professionalId)}/documents/${documentId}`);
  }
}
